/* charger, coming at you — the same eight phases read head-on.
 *
 * WHY THERE IS A SECOND BODY AT ALL. `flip` was applied to exactly two types
 * in the whole game and no enemy had up or down art, so a charger aimed north
 * and a charger aimed south were the same 117 pixels. The one thing a player
 * has to know about a charger is which line it has picked, and nothing on the
 * screen said it — only the hero had directional bodies.
 *
 * THE ANIMAL IS THE SAME ANIMAL. Phase for phase this is `charger.ts` turned
 * ninety degrees: 0 SET, 1 LOAD, 2 COIL, 3 UNLOAD, 4 DRIVE, 5 LAUNCH,
 * 6 STRIKE, 7 CATCH, on the same beats and under the same three rules — the
 * body moves every frame, row 13 is the floor and something is always on it,
 * and mass is traded rather than spent.
 *
 * WHAT CARRIES THE TELL WHEN THERE IS NO PROFILE. The side view says "aimed"
 * with the right edge of the horn row; head-on there is no right edge, so the
 * tell is the horn SPREAD and it moves the silhouette on both sides at once.
 * Measured as the outermost lit column of the horn rows, the schedule runs
 * 1/1/1/0/0/0/1/2 — out to the cell edge for the three frames of the rush,
 * drawn back in on the catch. The face carries the rest: two `e` eyes that
 * sink into the shoulders on the gather and come back up on the drive, at
 * rows 8/9/9/8/9/8/9/8.
 *
 * THE LEGS ARE FILLED, NOT OUTLINED. Drawn in `o` at two pixels wide they
 * were invisible — `o` is #241a24 and the grounds this stands on are darker
 * still — and a first pass had the animal ending at the belly. They are `R`
 * here and `spriteOutline` supplies the dark edge, which comes out the same
 * as the side view's `oRRo` in half the columns.
 *
 * Measured: steps 32/46/68/72/84/74/66/32 px, spread 1.2x the median and 2.6x
 * the smallest, mass 5.6% about a median of 108, floor drift 1 row.
 */

// phase 0 — SET. Horns neutral, head level, all four feet planted and even.
export const charger_down_0 = [
  '................',
  '................',
  '................',
  '................',
  '.oo..........oo.',
  '.ou..........uo.',
  '.oouoooooooouoo.',
  '..oRRRRRRRRRRo..',
  '..oRReRRRReRRo..',
  '..oRRRRRRRRRRo..',
  '.oRRrrrrrrrrRRo.',
  '.oRRRRRRRRRRRRo.',
  '.oRRRRRRRRRRRRo.',
  '..RR.RR..RR.RR..',
  '................',
  '................',
];

// phase 1 — LOAD. The head sinks a row into bunching shoulders and the horns
// draw a column in; the skull top thickens where the neck gathers under it.
export const charger_down_1 = [
  '................',
  '................',
  '................',
  '................',
  '.oo..........oo.',
  '.ouo........ouo.',
  '..ouoooooooouo..',
  '..ooRRRRRRRRoo..',
  '..oRRRRRRRRRRo..',
  '..oRReRRRReRRo..',
  '..oRRRRRRRRRRo..',
  '.oRRrrrrrrrrRRo.',
  '.oRRRRRRRRRRRRo.',
  '.RRR.RR..RR.RRR.',
  '................',
  '................',
];

// phase 2 — COIL. Deepest: the whole body drops a second row and spreads to
// both cell edges, paying for the two rows of leg it folds away with width.
export const charger_down_2 = [
  '................',
  '................',
  '................',
  '................',
  '................',
  '.oo..........oo.',
  '.ouo........ouo.',
  '..ouoooooooouo..',
  '..ooRRRRRRRRoo..',
  '.ooRReRRRReRRoo.',
  'ooRRRRRRRRRRRRoo',
  'ooRRrrrrrrrrRRoo',
  'ooRRRRRRRRRRRRoo',
  '.RRR.RR..RR.RRR.',
  '................',
  '................',
];

// phase 3 — UNLOAD. It commits. Horns snap out to the cell edge, the two
// inner legs fold up under the belly and the outer pair drives below row 13.
export const charger_down_3 = [
  '................',
  '................',
  '................',
  '................',
  'oo............oo',
  'ou............uo',
  'oouoooooooooouoo',
  '..oRRRRRRRRRRo..',
  '..oRReRRRReRRo..',
  '..oRRRRRRRRRRo..',
  '.oRRrrrrrrrrRRo.',
  '.oRRRRRRRRRRRRo.',
  '.oRRRRRRRRRRRRo.',
  '..RR..R..R..RR..',
  '..RR........RR..',
  '................',
];

// phase 4 — DRIVE. The head drops between the shoulders at full push, the
// ribcage narrows to twelve, and all four feet are up on their toes.
export const charger_down_4 = [
  '................',
  '................',
  '................',
  '................',
  '................',
  'oo............oo',
  'ou............uo',
  'oouoooooooooouoo',
  '.oRRRRRRRRRRRRo.',
  '.oRReRRRRRReRRo.',
  '.oRRRRRRRRRRRRo.',
  '..oRrrrrrrrrRo..',
  '.oRRRRRRRRRRRRo.',
  '..RR.RR..RR.RR..',
  '..RR.R....R.RR..',
  '................',
];

// phase 5 — LAUNCH. Head back up, horns still at the edge, and the legs are
// splayed to their widest — the frame with the most ground under it.
export const charger_down_5 = [
  '................',
  '................',
  '................',
  '................',
  'oo............oo',
  'ou............uo',
  'oouoooooooooouoo',
  '.oRRRRRRRRRRRRo.',
  '..oRReRRRReRRo..',
  '.oRRRRRRRRRRRRo.',
  '.oRRrrrrrrrrRRo.',
  '.oRRRRRRRRRRRRo.',
  '.RR..RR..RR..RR.',
  'RR...RR..RR...RR',
  '................',
  '................',
];

// phase 6 — STRIKE. The head slams down a row and the animal lands on two
// wide blocks of foot. Horns are back to neutral; the head has not recovered.
export const charger_down_6 = [
  '................',
  '................',
  '................',
  '................',
  '.oo..........oo.',
  '.ou..........uo.',
  '.oouoooooooouoo.',
  '..ooRRRRRRRRoo..',
  '..oRRRRRRRRRRo..',
  '.oRReRRRRRReRRo.',
  '..oRRRRRRRRRRo..',
  '.oRRrrrrrrrrRRo.',
  '.oRRRRRRRRRRRRo.',
  '..RRR......RRR..',
  '..RRR......RRR..',
  '................',
];

// phase 7 — CATCH. Gathered: horns drawn fully in, the skull narrow, the
// barrel out to both edges and the legs passing under the chest. Next: set.
export const charger_down_7 = [
  '................',
  '................',
  '................',
  '................',
  '..oo........oo..',
  '..ou........uo..',
  '..oouoooooouoo..',
  '...oRRRRRRRRo...',
  '...oReRRRReRo...',
  '..oRRRRRRRRRRo..',
  'ooRRrrrrrrrrRRoo',
  'ooRRRRRRRRRRRRoo',
  '.oRRRRRRRRRRRRo.',
  '...RR.RR.RR.RR..',
  '................',
  '................',
];
